import React, { useState, useEffect } from 'react';
import { API_URL } from "../url_config";
import {
  Button,
  Table,
  Input,
  Space,
  message,
  Card,
  Tag,
  Drawer,
  Form,
  Typography,
} from 'antd';
import {
  ReloadOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { NavigationStepsKeolisNoc } from "../components/navbars/navbar_keolis_noc";

const { Title } = Typography;

export default function PierreKeolisNoc() {
  const apiUrl = `${API_URL}/validations/pb_cnx/?ot_tag=keolis_noc`;

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Token ${token}` } : {};
  };

  const [data, setData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingField, setEditingField] = useState({ id: null, field: null });
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [newRow, setNewRow] = useState({
    email: '',
    poste: '',
    applications: '',
    comportement: '',
    prise_en_compte: false,
    traitee_verifiee: false,
    commentaire: '',
    ot_tag: 'keolis_noc',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error('Erreur de chargement');
      const result = await res.json();
      setData(result);
    } catch (err) {
      console.error('Erreur fetch:', err);
      message.error('Impossible de charger les donnees.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [apiUrl]);

  const handlePatch = async (id, field, value) => {
    try {
      const res = await fetch(`${API_URL}/validations/pb_cnx/${id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error('Erreur PATCH');
      setData((prev) =>
        prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
      );
      message.success('Mise a jour reussie');
      setEditingField({ id: null, field: null });
    } catch {
      message.error('Erreur lors de la mise a jour');
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/validations/pb_cnx/${id}/`, { method: 'DELETE' });
      setData((prev) => prev.filter((item) => item.id !== id));
      message.success('Ligne supprimee');
    } catch {
      message.error('Suppression echouee');
    }
  };

  const handleAddRow = async () => {
    if (!newRow.email || !newRow.poste || !newRow.applications) {
      message.warning('Veuillez remplir les champs obligatoires.');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/validations/pb_cnx/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(newRow),
      });
      if (!res.ok) throw new Error();
      await fetchData();
      setDrawerVisible(false);
      message.success('Nouvelle ligne ajoutee');
      setNewRow({
        email: '',
        poste: '',
        applications: '',
        comportement: '',
        prise_en_compte: false,
        traitee_verifiee: false,
        commentaire: '',
        ot_tag: 'keolis_noc',
      });
    } catch {
      message.error('Impossible d’ajouter la ligne.');
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'email' ? (
          <Space>
            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 200 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'email', editValue)} />
          </Space>
        ) : (
          <Space>
            <div>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'email' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: 'Poste',
      dataIndex: 'poste',
      key: 'poste',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'poste' ? (
          <Space>
            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 150 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'poste', editValue)} />
          </Space>
        ) : (
          <Space>
            <div>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'poste' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: 'Applications',
      dataIndex: 'applications',
      key: 'applications',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'applications' ? (
          <Space>
            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 150 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'applications', editValue)} />
          </Space>
        ) : (
          <Space>
            <div>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'applications' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: "Comportement observe (Description des manipulations, Message d'erreur, etc)",
      dataIndex: 'comportement',
      key: 'comportement',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'comportement' ? (
          <Space>
            <Input.TextArea rows={2} value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 250 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'comportement', editValue)} />
          </Space>
        ) : (
          <Space>
            <div style={{ whiteSpace: 'pre-wrap', maxWidth: 250 }}>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'comportement' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: 'Commentaire (reserve au support)',
      dataIndex: 'commentaire',
      key: 'commentaire',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'commentaire' ? (
          <Space>
            <Input.TextArea rows={2} value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 250 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'commentaire', editValue)} />
          </Space>
        ) : (
          <Space>
            <div style={{ whiteSpace: 'pre-wrap', maxWidth: 250 }}>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'commentaire' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: 'Etat',
      key: 'etat',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Tag
            color={record.prise_en_compte ? 'blue' : 'default'}
            icon={record.prise_en_compte ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#aaa" />}
            style={{ cursor: 'pointer', width: 'fit-content', padding: '4px 10px', fontSize: '13px', borderRadius: 8 }}
            onClick={() => handlePatch(record.id, 'prise_en_compte', !record.prise_en_compte)}
          >
            Verifie : {record.prise_en_compte ? 'Oui' : 'Non'}
          </Tag>

          <Tag
            color={record.traitee_verifiee ? 'green' : 'red'}
            icon={record.traitee_verifiee ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#ff4d4f" />}
            style={{ cursor: 'pointer', width: 'fit-content', padding: '4px 10px', fontSize: '13px', borderRadius: 8 }}
            onClick={() => handlePatch(record.id, 'traitee_verifiee', !record.traitee_verifiee)}
          >
            Traite CDS : {record.traitee_verifiee ? 'Oui' : 'Non'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <NavigationStepsKeolisNoc selectedKey="incidents_habilitations" />

      <Card bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderRadius: 12 }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} align="center">
          <Title level={3} style={{ margin: 0 }}>
            Declaration de problemes de connexion — Keolis Noc
          </Title>

          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              Rafraichir
            </Button>
            <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => setDrawerVisible(true)}>
              Ajouter
            </Button>
          </Space>
        </Space>

        <Table style={{ marginTop: 20 }} dataSource={data} columns={columns} rowKey="id" pagination={false} />
      </Card>

      <Drawer title="Ajout informations" open={drawerVisible} onClose={() => setDrawerVisible(false)} width={400}>
        <Form layout="vertical">
          <Form.Item label="Email">
            <Input name="email" value={newRow.email} onChange={(e) => setNewRow({ ...newRow, email: e.target.value })} placeholder="ex: utilisateur@ratp.fr" />
          </Form.Item>

          <Form.Item label="Poste concerne">
            <Input name="poste" value={newRow.poste} onChange={(e) => setNewRow({ ...newRow, poste: e.target.value })} placeholder="Poste" />
          </Form.Item>

          <Form.Item label="Application(s)">
            <Input name="applications" value={newRow.applications} onChange={(e) => setNewRow({ ...newRow, applications: e.target.value })} placeholder="Applications concernees" />
          </Form.Item>

          <Form.Item label="Comportement observe (Description des manipulations, Message d'erreur, etc)">
            <Input.TextArea rows={4} name="comportement" value={newRow.comportement} onChange={(e) => setNewRow({ ...newRow, comportement: e.target.value })} />
          </Form.Item>

          <Form.Item label="Commentaire (support)">
            <Input.TextArea rows={2} name="commentaire" value={newRow.commentaire} onChange={(e) => setNewRow({ ...newRow, commentaire: e.target.value })} />
          </Form.Item>

          <Button type="primary" block icon={<PlusCircleOutlined />} onClick={handleAddRow}>
            Ajouter
          </Button>
        </Form>
      </Drawer>
    </div>
  );
}